import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  AddRounded,
  OpenInNewRounded,
  UploadFileOutlined,
  DeleteRounded,
  SearchRounded,
  FilterListRounded,
  ClearRounded,
  ExitToAppRounded,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  documentId,
  type DocumentData,
  type DocumentSnapshot,
} from 'firebase/firestore';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../hooks/use-permissions';
import { CreateAdmissionDialog } from './components/CreateAdmissionDialog';
import { SubmitAdmissionDocumentsDialog } from './components/SubmitAdmissionDocumentsDialog';
import { WithdrawStudentDialog } from './components/WithdrawStudentDialog';
import { MissingTenantView } from '../../components/common/MissingTenantView';
import { admissionsApi } from '../../lib/api/admissions.api';
import { formatDate, formatMoney } from '../../lib/i18n-format';

interface AdmissionRow {
  id: string;
  status: string;
  personId: string; // resolved to name after fetch
  personIdRaw: string; // original Firestore ID for navigation
  programId: string;
  termId: string;
  createdAt: { toDate(): Date } | string | null | undefined;
}

/** Parses a Firestore Timestamp or ISO string into a Date (returns null on failure). */
function parseFirestoreDate(value: { toDate(): Date } | string | null | undefined): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  submitted: 'info',
  under_review: 'warning',
  awaiting_applicant: 'info',
  accepted: 'success',
  rejected: 'error',
  waitlisted: 'warning',
  withdrawn: 'default',
  cancelled: 'default',
};

type AdmSortField = 'personId' | 'programId' | 'termId' | 'status' | 'createdAt';
type SortDir = 'asc' | 'desc';

export function AdmissionsListPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId, currentMembership } = useTenantStore();
  const { hasRole, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState<AdmissionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [docsAdmissionId, setDocsAdmissionId] = useState<string | undefined>(undefined);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>(undefined);
  const [confirmDeleteStatus, setConfirmDeleteStatus] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSnackbar, setDeleteSnackbar] = useState<{ open: boolean; success: boolean }>({
    open: false,
    success: true,
  });

  // Withdraw state — delegated to WithdrawStudentDialog (multi-step wizard).
  const [withdrawTarget, setWithdrawTarget] = useState<{ id: string; personName: string } | null>(
    null,
  );
  const [withdrawSnackbar, setWithdrawSnackbar] = useState<{
    open: boolean;
    success: boolean;
    msg?: string;
  } | null>(null);

  // Search / filter / sort
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState<AdmSortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function handleSort(field: AdmSortField) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function clearFilters() {
    setSearch('');
    setFilterStatus('');
  }

  const canDelete = isAdmin || hasRole('registrar');
  const canWithdraw = isAdmin || hasRole('registrar');

  const hasActiveFilters = search || filterStatus;

  const displayed = useMemo(() => {
    const term = search.toLowerCase();
    let result = admissions.filter((a) => {
      const matchSearch =
        !term ||
        a.personId?.toLowerCase().includes(term) ||
        a.programId?.toLowerCase().includes(term) ||
        a.termId?.toLowerCase().includes(term);
      const matchStatus = !filterStatus || a.status === filterStatus;
      return matchSearch && matchStatus;
    });

    result = [...result].sort((a, b) => {
      if (sortField === 'createdAt') {
        const da = parseFirestoreDate(a.createdAt)?.getTime() ?? 0;
        const db2 = parseFirestoreDate(b.createdAt)?.getTime() ?? 0;
        return sortDir === 'asc' ? da - db2 : db2 - da;
      }
      const av = (a[sortField] as string) ?? '';
      const bv = (b[sortField] as string) ?? '';
      const cmp = av.localeCompare(bv, 'es', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [admissions, search, filterStatus, sortField, sortDir]);

  const handleDelete = async () => {
    if (!confirmDeleteId || !currentTenantId) return;
    setIsDeleting(true);
    try {
      await admissionsApi.delete({ tenantId: currentTenantId, admissionId: confirmDeleteId });
      setDeleteSnackbar({ open: true, success: true });
    } catch {
      setDeleteSnackbar({ open: true, success: false });
    } finally {
      setIsDeleting(false);
      setConfirmDeleteId(undefined);
      setConfirmDeleteStatus('');
    }
  };

  const openDeleteDialog = (id: string, status: string) => {
    setConfirmDeleteId(id);
    setConfirmDeleteStatus(status);
  };

  const closeDeleteDialog = () => {
    if (!isDeleting) {
      setConfirmDeleteId(undefined);
      setConfirmDeleteStatus('');
    }
  };

  // A reviewer-only user sees only their assigned admissions.
  // Admins, registrars and other privileged roles see all.
  const isReviewerOnly = hasRole('reviewer') && !isAdmin && !hasRole('registrar');

  useEffect(() => {
    if (!currentTenantId) {
      setLoading(false);
      return;
    }

    // For reviewer-only users: filter to only the admissions assigned to them.
    // Admissions store a `reviewerIds` array of personIds, set when the review starts.
    // We resolve the current user's personId from their membership document.
    const reviewerPersonId = isReviewerOnly
      ? ((currentMembership as unknown as Record<string, unknown>)?.['personId'] as
          | string
          | undefined)
      : undefined;

    // If reviewer but no personId linked yet, show an empty list
    if (isReviewerOnly && !reviewerPersonId) {
      console.warn('[AdmissionsListPage] Reviewer has no personId — showing empty list.');
      setAdmissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = reviewerPersonId
      ? query(
          collection(db, `tenants/${currentTenantId}/admissions`),
          where('reviewerIds', 'array-contains', reviewerPersonId),
        )
      : query(collection(db, `tenants/${currentTenantId}/admissions`));

    const unsubscribe = onSnapshot(
      q,
      async (snap) => {
        try {
          const rawAdmissions = snap.docs.map((d: DocumentData) => {
            const data = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              ...(data as Omit<AdmissionRow, 'id' | 'personIdRaw'>),
              personIdRaw: data['personId'] as string,
            };
          });

          // Gather unique IDs
          const personIds = [...new Set(rawAdmissions.map((a) => a.personIdRaw).filter(Boolean))];
          const programIds = [...new Set(rawAdmissions.map((a) => a.programId).filter(Boolean))];
          const termIds = [...new Set(rawAdmissions.map((a) => a.termId).filter(Boolean))];

          // Batch the reference reads with `where(documentId() in [...])` instead
          // of one getDoc per id. At 1000+ admissions the old N+1 fired up to
          // 1000 separate person reads; this collapses each collection to
          // ceil(n/10) queries (Firestore caps an `in` filter at 10 values).
          const fetchByIds = async (col: string, ids: string[]) => {
            if (ids.length === 0) return [] as DocumentSnapshot[];
            const chunks: string[][] = [];
            for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
            const snaps = await Promise.all(
              chunks.map((chunk) =>
                getDocs(
                  query(
                    collection(db, `tenants/${currentTenantId}/${col}`),
                    where(documentId(), 'in', chunk),
                  ),
                ),
              ),
            );
            return snaps.flatMap((s) => s.docs);
          };

          const [peopleDocs, programDocs, termDocs] = await Promise.all([
            fetchByIds('people', personIds),
            fetchByIds('programs', programIds),
            fetchByIds('terms', termIds),
          ]);

          const peopleMap = new Map<string, string>();
          peopleDocs.forEach((d) => {
            if (d.exists()) {
              const data = d.data();
              peopleMap.set(
                d.id,
                `${data?.firstName || ''} ${data?.lastName || ''}`.trim() || d.id,
              );
            }
          });

          const programsMap = new Map<string, string>();
          programDocs.forEach((d) => {
            if (d.exists()) programsMap.set(d.id, d.data()?.name || d.id);
          });

          const termsMap = new Map<string, string>();
          termDocs.forEach((d) => {
            if (d.exists()) termsMap.set(d.id, d.data()?.name || d.id);
          });

          setAdmissions(
            rawAdmissions
              .map((a) => ({
                ...a,
                personId: peopleMap.get(a.personIdRaw) || a.personIdRaw,
                programId: programsMap.get(a.programId) || a.programId,
                termId: termsMap.get(a.termId) || a.termId,
              }))
              .sort((a, b) => {
                const dateA = parseFirestoreDate(a.createdAt)?.getTime() ?? 0;
                const dateB = parseFirestoreDate(b.createdAt)?.getTime() ?? 0;
                return dateB - dateA;
              }),
          );
        } catch (err) {
          console.error('[AdmissionsListPage] Error resolving admission data:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('[AdmissionsListPage] Firestore snapshot error:', err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [currentTenantId, isReviewerOnly, currentMembership]);

  if (!currentTenantId) {
    return <MissingTenantView />;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="white">
            {t('admissions.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? `${displayed.length} de ${admissions.length} solicitudes`
              : t('admissions.subtitle', { count: admissions.length })}
          </Typography>
        </Box>
        {!isReviewerOnly && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setOpenCreate(true)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            {t('admissions.new')}
          </Button>
        )}
      </Stack>

      {/* ── Search + Filters bar ──────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        mb={2}
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <TextField
          placeholder="Buscar por persona, programa o período..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: 'action.active' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>
            <FilterListRounded sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Estado
          </InputLabel>
          <Select
            value={filterStatus}
            label="⠀Estado"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="draft">Borrador</MenuItem>
            <MenuItem value="submitted">Enviada</MenuItem>
            <MenuItem value="under_review">En revisión</MenuItem>
            <MenuItem value="awaiting_applicant">Esperando postulante</MenuItem>
            <MenuItem value="accepted">Aceptada</MenuItem>
            <MenuItem value="rejected">Rechazada</MenuItem>
            <MenuItem value="waitlisted">En lista de espera</MenuItem>
            <MenuItem value="withdrawn">Retirada</MenuItem>
            <MenuItem value="cancelled">Cancelada</MenuItem>
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Tooltip title="Limpiar filtros">
            <IconButton size="small" onClick={clearFilters} sx={{ mt: 0.5 }}>
              <ClearRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, boxShadow: 'none', overflowX: 'auto' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('admissions.columns.id')}
                </TableCell>
                <TableCell sortDirection={sortField === 'personId' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'personId'}
                    direction={sortField === 'personId' ? sortDir : 'asc'}
                    onClick={() => handleSort('personId')}
                  >
                    {t('admissions.columns.person')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'programId' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'programId'}
                    direction={sortField === 'programId' ? sortDir : 'asc'}
                    onClick={() => handleSort('programId')}
                  >
                    {t('admissions.columns.program')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ display: { xs: 'none', md: 'table-cell' } }}
                  sortDirection={sortField === 'termId' ? sortDir : false}
                >
                  <TableSortLabel
                    active={sortField === 'termId'}
                    direction={sortField === 'termId' ? sortDir : 'asc'}
                    onClick={() => handleSort('termId')}
                  >
                    {t('admissions.columns.term')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'status' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDir : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('admissions.columns.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  sortDirection={sortField === 'createdAt' ? sortDir : false}
                >
                  <TableSortLabel
                    active={sortField === 'createdAt'}
                    direction={sortField === 'createdAt' ? sortDir : 'asc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    {t('admissions.columns.date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.disabled', py: 6 }}>
                    {hasActiveFilters
                      ? 'No se encontraron solicitudes con esos filtros.'
                      : t('admissions.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                displayed.map((a) => (
                  <TableRow
                    key={a.id}
                    hover
                    onClick={() => navigate(`/admissions/${a.id}`)}
                    sx={{
                      '& td': { color: 'text.primary' },
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.05)' },
                    }}
                  >
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography fontFamily="monospace" fontSize={13} color="text.secondary">
                        {a.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{a.personId}</TableCell>
                    <TableCell>{a.programId}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {a.termId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`admissions.status.${a.status}`, a.status)}
                        size="small"
                        color={STATUS_COLORS[a.status] ?? 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'text.secondary' }}
                    >
                      {formatDate(parseFirestoreDate(a.createdAt)) || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                        {/* Upload docs — only for drafts; stop propagation to avoid row nav */}
                        {a.status === 'draft' && (
                          <Tooltip title={t('admissions.actions.upload', 'Subir documentos')}>
                            <IconButton
                              size="small"
                              sx={{ color: 'text.secondary' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDocsAdmissionId(a.id);
                              }}
                            >
                              <UploadFileOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('admissions.actions.view', 'Ver detalle')}>
                          <IconButton
                            size="small"
                            sx={{ color: 'primary.main' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admissions/${a.id}`);
                            }}
                          >
                            <OpenInNewRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Withdraw — visible to admin/registrar only for accepted admissions */}
                        {canWithdraw && a.status === 'accepted' && (
                          <Tooltip title="Retirar alumno">
                            <IconButton
                              size="small"
                              sx={{ color: 'warning.main' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setWithdrawTarget({ id: a.id, personName: a.personId });
                              }}
                            >
                              <ExitToAppRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Delete — visible to admin/registrar for ALL statuses */}
                        {canDelete && (
                          <Tooltip title={t('admissions.actions.delete', 'Eliminar solicitud')}>
                            <IconButton
                              size="small"
                              sx={{ color: 'error.light' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteDialog(a.id, a.status);
                              }}
                            >
                              <DeleteRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateAdmissionDialog open={openCreate} onClose={() => setOpenCreate(false)} />

      <SubmitAdmissionDocumentsDialog
        open={!!docsAdmissionId}
        onClose={() => setDocsAdmissionId(undefined)}
        admissionId={docsAdmissionId}
      />

      {/* ─── Delete Confirmation Dialog ─────────────────────────────── */}
      <Dialog open={!!confirmDeleteId} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmDeleteStatus === 'accepted'
            ? t('admissions.confirmDelete.titleAccepted', '⚠️ Eliminar admisión aceptada')
            : t('admissions.confirmDelete.title', '¿Eliminar solicitud?')}
        </DialogTitle>
        <DialogContent>
          {confirmDeleteStatus === 'accepted' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'admissions.confirmDelete.acceptedWarning',
                'Esta admisión está aceptada. Al eliminarla, la persona perderá el estado de estudiante y volverá a ser clasificada como Externo.',
              )}
            </Alert>
          )}
          <DialogContentText>
            {t(
              'admissions.confirmDelete.body',
              'Esta acción no se puede deshacer. La solicitud será eliminada permanentemente.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isDeleting}>
            {t('admissions.confirmDelete.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('admissions.confirmDelete.confirm', 'Sí, eliminar')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Snackbar ────────────────────────────────────────── */}
      <Snackbar
        open={deleteSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setDeleteSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={deleteSnackbar.success ? 'success' : 'error'}
          onClose={() => setDeleteSnackbar((s) => ({ ...s, open: false }))}
        >
          {deleteSnackbar.success
            ? t('admissions.deleteSuccess', 'Solicitud eliminada correctamente')
            : t('admissions.deleteError', 'No se pudo eliminar la solicitud')}
        </Alert>
      </Snackbar>

      {withdrawTarget && currentTenantId && (
        <WithdrawStudentDialog
          open={!!withdrawTarget}
          tenantId={currentTenantId}
          admissionId={withdrawTarget.id}
          studentName={withdrawTarget.personName}
          onClose={() => setWithdrawTarget(null)}
          onSuccess={(summary) => {
            setWithdrawSnackbar({
              open: true,
              success: true,
              // En la moneda del plan; sin plan no hay monto que devolver.
              msg:
                summary.refundAmount > 0 && summary.currency
                  ? t('admissions.withdrawDoneRefund', {
                      name: withdrawTarget.personName,
                      amount: formatMoney(summary.refundAmount, summary.currency, {
                        maximumFractionDigits: 0,
                      }),
                    })
                  : t('admissions.withdrawDone', { name: withdrawTarget.personName }),
            });
          }}
        />
      )}

      {/* ─── Withdraw Snackbar ─────────────────────────────────────────── */}
      {withdrawSnackbar && (
        <Snackbar
          open={withdrawSnackbar.open}
          autoHideDuration={5000}
          onClose={() => setWithdrawSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={withdrawSnackbar.success ? 'success' : 'error'}
            onClose={() => setWithdrawSnackbar(null)}
          >
            {withdrawSnackbar.msg}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
